export interface AccountFeatures {
  email_delivery_enabled: boolean;
  email_verification_required: boolean;
  password_reset_enabled: boolean;
}

export interface AcceptedResponse {
  accepted: boolean;
  delivery_enabled: boolean;
}

export interface AccountLinkTokens {
  reset: string | null;
  verify: string | null;
}

export function accountLinkTokensFromFragment(fragment: string): AccountLinkTokens {
  const parameters = new URLSearchParams(fragment.startsWith("#") ? fragment.slice(1) : "");
  return {
    reset: parameters.get("reset"),
    verify: parameters.get("verify"),
  };
}

function requireToken(token: string): string {
  if (token.length < 16 || token.length > 4096) throw new Error("Der Account-Link ist ungültig oder abgelaufen.");
  return token;
}

async function jsonRequest<T>(
  path: string,
  method: "GET" | "POST",
  payload: Record<string, string> | undefined,
  fetcher: typeof fetch,
): Promise<T> {
  const response = await fetcher(path, {
    method,
    cache: "no-store",
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      ...(payload ? { "Content-Type": "application/json" } : {}),
    },
    ...(payload ? { body: JSON.stringify(payload) } : {}),
  });
  if (!response.ok) throw new Error("Der Account-Link ist ungültig, abgelaufen oder der Dienst ist nicht erreichbar.");
  if (response.status === 204) return undefined as T;
  return await response.json() as T;
}

export function loadAccountFeatures(fetcher: typeof fetch = fetch): Promise<AccountFeatures> {
  return jsonRequest<AccountFeatures>("/api/v1/accounts/features", "GET", undefined, fetcher);
}

export function requestPasswordReset(email: string, fetcher: typeof fetch = fetch): Promise<AcceptedResponse> {
  const normalized = email.trim().toLowerCase();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/u.test(normalized)) {
    throw new Error("Bitte gib eine gültige E-Mail-Adresse ein.");
  }
  return jsonRequest<AcceptedResponse>(
    "/api/v1/accounts/password-reset/request",
    "POST",
    { email: normalized },
    fetcher,
  );
}

export function confirmPasswordReset(
  token: string,
  newPassword: string,
  fetcher: typeof fetch = fetch,
): Promise<void> {
  if (newPassword.length < 10) throw new Error("Das neue Passwort braucht mindestens 10 Zeichen.");
  return jsonRequest<void>(
    "/api/v1/accounts/password-reset/confirm",
    "POST",
    { token: requireToken(token), new_password: newPassword },
    fetcher,
  );
}

export function confirmEmailVerification(token: string, fetcher: typeof fetch = fetch): Promise<{ verified: boolean }> {
  return jsonRequest<{ verified: boolean }>(
    "/api/v1/accounts/email/verification/confirm",
    "POST",
    { token: requireToken(token) },
    fetcher,
  );
}
